import { FC, useState } from "react";
import { Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useFactoryCreditBalance, useFactoryCodCreditInfo } from "hooks/useFactoryCredit";
import { getQuotaSnapshot, getMainCreditLineDisplay, resolveCreditDebtSnapshot } from "utils/creditDisplay";
import ExpiringCreditModal from "components/ManageCredit/ExpiringCreditModal";

const { Text } = Typography;

/** bordered=false = สไตล์เดียวกับ OrderCodCreditCard.tsx (การ์ดเครดิตมุมขวาบนของ "คำสั่งซื้อ" — ไม่ตีเส้น
 * padding/margin กระชับกว่า) ใช้ตอนวางในตำแหน่งเดียวกัน (RightPane ของแถบหัวเรื่อง+แท็บ) — ค่า default
 * ยังคงเป็นแบบมีเส้นกรอบเหมือนเดิมเพื่อไม่กระทบหน้าอื่นที่ใช้ ParcelCreditCard อยู่แล้ว (ManageParcel เดิม) */
const CardWrapper = styled.div<{ $bordered: boolean }>`
  width: 100%;
  padding: ${(p) => (p.$bordered ? "12px 16px" : "8px 16px")};
  background: #fff;
  border: ${(p) => (p.$bordered ? "1px solid #f0f0f0" : "none")};
  border-radius: 8px;
  margin-bottom: ${(p) => (p.$bordered ? "12px" : "0")};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

type ParcelCreditCardProps = {
  /** default true (ของเดิม ตีเส้น) — ส่ง false เพื่อให้เหมือน OrderCodCreditCard.tsx (ไม่ตีเส้น) */
  bordered?: boolean;
};

const ParcelCreditCard: FC<ParcelCreditCardProps> = ({ bordered = true }) => {
  const { data } = useFactoryCreditBalance();
  // ดึง snapshot จาก cod-info (cached — ไม่มี extra API call) สูตรเดียวกับ OrderCodCreditCard
  // remaining_quota จาก API; expiring_quota = min(lot×mult, remaining_quota)
  const { data: codInfo } = useFactoryCodCreditInfo();
  const multiplier = codInfo?.cod_credit_multiplier ?? 2;
  const [expiringModalOpen, setExpiringModalOpen] = useState(false);

  if (!data) return null;

  const available = Number(data.available_credit ?? 0);
  const pending = Number(codInfo?.pending_cod_credit ?? 0);
  const remainingQuota = Number(
    codInfo?.remaining_quota ?? getQuotaSnapshot(available, pending, multiplier).remaining_quota
  );

  const debtSnap = resolveCreditDebtSnapshot({
    availableCredit: data.available_credit,
    availableCreditAlt: codInfo?.available_credit,
    hasCreditDebt: data.has_credit_debt,
    hasCreditDebtAlt: codInfo?.has_credit_debt,
    debtAmount: data.debt_amount,
    debtAmountAlt: codInfo?.debt_amount,
    debtDisplay: data.debt_display,
    debtDisplayAlt: codInfo?.debt_display,
    multiplier,
  });

  const mainCredit = getMainCreditLineDisplay({
    availableRaw: debtSnap.availableRaw,
    multiplier,
    remainingQuota,
    debtDisplay: debtSnap.debtDisplay,
    debtAmount: debtSnap.debtAmount,
    hasCreditDebt: debtSnap.hasCreditDebt,
  });

  return (
    <CardWrapper $bordered={bordered}>
      <Text strong style={{ fontSize: 20, lineHeight: 1.3 }}>
        เครดิตที่ใช้ได้
      </Text>
      <Text strong style={{ fontSize: 26, lineHeight: 1.3 }}>
        <span style={{ color: mainCredit.color }}>{mainCredit.valueText}</span>
        <span style={{ color: "#1890ff", fontSize: 20 }}> Credit(s)</span>
      </Text>
      {/* ทั้งบรรทัดคลิกเปิด modal ได้ ไม่ใช่แค่ไอคอน — รายละเอียดเต็ม (ยอดรวม + breakdown ตามวันหมดอายุ)
       * อยู่ใน ExpiringCreditModal แล้ว ไม่ต้องสรุปตัวเลข "กำลังหมดอายุ"/"วันหมดอายุ" ซ้ำไว้ตรงนี้อีก —
       * ตัวเล็กกว่า 2 บรรทัดบน (2026-08-26) เพราะเป็นแค่ลิงก์รอง ไม่ใช่ข้อมูลหลัก — ชุดเดียวกับ
       * OrderCodCreditCard.tsx */}
      <Text
        style={{ fontSize: 12, color: "#1890ff", marginTop: 4, cursor: "pointer" }}
        onClick={() => setExpiringModalOpen(true)}
      >
        รายละเอียด
        <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 11 }} />
      </Text>
      <ExpiringCreditModal open={expiringModalOpen} onClose={() => setExpiringModalOpen(false)} />
    </CardWrapper>
  );
};

export default ParcelCreditCard;
